<template>
    <div
    class="product-selector-wrapper"
    :class="[variant, {
        'mobile': getMobileAppState,
        'apply-mobile-header-product-slider': applyMobileHeaderProductSlider,
        'invert-title-and-description': invertTitleAndDescription,
        'has-click-on-product': params.hasClickOnProduct
    }]"
    @click="emit('clicked', params)">
        <div class="buttons-right-position-helper">
            <!-- More Options -->
            <dropdown-more-options
            v-if="params.moreOptions"
            :options="params.moreOptions"
            @clicked="emit('moreOptionsClicked', $event)"/>
            <!-- Button -->
            <button-quaternary
            v-if="params.rightTopButton"
            :icon="params.rightTopButton.icon"
            @clicked="emit('rightTopButtonClicked')"/>
        </div>
        <!-- Product Image -->
        <div v-if="params.imageUrl && !applyMobileHeaderProductSlider" class="product-image" :style="{'background-image': `url(${params.imageUrl})`}"/>
        <!-- Product Info -->
        <div class="product-info-wrapper">
            <!-- Title -->
            <div v-if="!applyMobileHeaderProductSlider || (applyMobileHeaderProductSlider && params.imageUrl)" class="title">{{ params.title }}</div>
            <!-- Description -->
            <div v-if="invertTitleAndDescription" class="description" :class="params.descriptionClass">{{ params.description }}</div>
            <!-- Elements that can be hidden -->
            <div
            v-if="params.description && !invertTitleAndDescription || params.disclaimer || params.list"
            ref="elementsHiddenWrapper"
            class="elements-hidden-wrapper"
            :class="{ 'remove-max-height': showAllInfo, 'add-after': !showAllInfo, 'padding-bottom': needsShowHideButton }">
                <!-- Description -->
                <div v-if="!invertTitleAndDescription && !creditProduct" class="description" :class="params.descriptionClass">{{ params.description }}</div>
                <!-- Infobox -->
                <infobox-generic
                v-if="params.disclaimer && params.hasPlafond"
                class="information"
                :class="'mobile-information'"
                :label="params.disclaimer"/>
                <!-- Product List -->
                <text-block-list v-if="params.list" :list="params.list" :isNumbered="params.listIsNumbered"/>
                <!-- Divider to hide/show info -->
                <divider-generic
                v-if="needsShowHideButton"
                dividerSize="small"
                :key="showAllInfo"
                :icon="showAllInfo ? 'arrow-up' : 'arrow-down'"
                :title="showAllInfo ? t('commonButtons.hideInfo') : t('commonButtons.readMore')"
                :applyBorder="false"
                @click="showAllInfo = !showAllInfo"/>
            </div>

            <!-- Info Sheet Button only on mobile version -->
            <button-raised
            v-if="getMobileAppState"
            :text="params.infoSheetUrlBtnText"
            buttonType="primary"
            icon="download"
            @clicked="downloadClickHandler"/>

            <!-- Text Blocks Group -->
            <div v-if="params.textBlocks" class="text-blocks-container">
                <!-- Multiple Text Block -->
                <text-block-generic
                v-for="(entry, index) in params.textBlocks" :key="index.key"
                :params="entry"
                :applyEllipsis="!getMobileAppState"
                :mask="entry.mask"/>
            </div>

            <!-- Buttons -->
            <div v-if="params.infoSheetUrl || params.secondBtnText" class="buttons-wrapper">
                <!-- Info Sheet Button -->
                <button-tertiary
                v-if="params.infoSheetUrl"
                icon="download"
                :label="params.infoSheetUrlBtnText"
                @clicked="downloadClickHandler"/>
                <!-- Second Button -->
                <button-primary
                v-if="!applyMobileHeaderProductSlider"
                buttonSize="medium"
                icon="arrow-right-full"
                iconPosition="right"
                :class="'mobile-constitute-button'"
                :label="params.secondBtnText"
                @click="params.secondBtnClicked(params)"/>
            </div>
        </div>
        <!-- Component to handle download logic based on platform -->
		<download-logic-component
        :downloadLink="downloadLink"
        :customSuccessMessage="params.infoSheetCustomSuccessMessage"
        @clearDownloadLink="downloadLink = null"/>
    </div>
</template>

<script setup>
import DropdownMoreOptions from '@/components/generic/dropdowns/moreOptions/MoreOptions'
import ButtonPrimary from '@/components/generic/buttons/Primary'
import ButtonTertiary from '@/components/generic/buttons/Tertiary'
import ButtonQuaternary from '@/components/generic/buttons/Quaternary'
import InfoboxGeneric from '@/components/generic/infobox/Generic'
import TextBlockList from '@/components/generic/textBlocks/List'
import DividerGeneric from '@/components/generic/dividers/Generic'
import TextBlockGeneric from '@/components/generic/textBlocks/Generic'
import DownloadLogicComponent from '@/components/generic/DownloadLogicComponent'

/* Vue */
import { ref, onMounted } from 'vue'

/* Store */
import { mapGetters } from '@/store/helpers/mappers'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* mapActions & mapGetters */
const { getMobileAppState } = mapGetters()

const emit = defineEmits(['clicked', 'moreOptionsClicked', 'rightTopButtonClicked'])

/* Props
@applyMobileHeaderProductSlider - prop to force product header slider on mobile
@variant {String} - variant for product [without-box (default), with-box]
@invertTitleAndDescription {Boolean} prop to invert order of title and description
@creditProduct {Boolean} prop to show product description on item card
@params { Object } - object with card info, needs to have:
    - hasClickOnProduct {Boolean}
	- title { String }
	- description { String }
    - descriptionClass { String } - can be [tiny, bold]
    - disclaimer
    - hasPlafond { Boolean }
    - infoSheetUrl { String } - href to download infoSheet
	- infoSheetUrlBtnText { String } - text to display on infoSheet button text
    - secondBtnText { String } - text to display on second button (second)
    - secondBtnClicked { Function } - function to aplly when the secound button is clicked
    - textBlocks { Array } - each item is an Object
        - label { String }
        - content { String }
        - mask { Object }
    - moreOptions {Array}- array of options to use in moreOptions
    - infoSheetCustomSuccessMessage {String} - custom message for success toast
*/
const props = defineProps({
    applyMobileHeaderProductSlider: Boolean,
    variant: String,
    invertTitleAndDescription: Boolean,
    creditProduct: Boolean,
    params: Object
})

const showAllInfo = ref(true)
const needsShowHideButton = ref(true)
const elementsHiddenWrapper = ref('')

/* Download Logic */
const downloadLink = ref(null)

const downloadClickHandler = () => {
    /* When running local files that are in https domains will bring CORS error */
	downloadLink.value = props.params.infoSheetUrl
}

onMounted(() => {
    if (elementsHiddenWrapper.value.scrollHeight < 126) needsShowHideButton.value = false
})
</script>

<style lang="scss" scoped>
.product-selector-wrapper {
    width: 100%;
    position: relative;

    display: flex;
    gap: 24px;

    &.without-box {
        padding: 16px 0 32px 0;

        border-bottom: 1px solid var(--secondary-light-2);

        &:first-of-type {
            padding-top: 32px;
        }

        &:last-of-type {
            border-bottom: unset;
        }
    }

    &.with-box {
        padding: 16px;

        border: 1px solid var(--secondary-light-1);
        border-radius: 8px;
    }

    &.has-click-on-product {
        cursor: pointer;
    }

    &.invert-title-and-description {
        .title {
            order: 2;
            margin-bottom: 0;
        }

        .description {
            order: 1;
            margin-bottom: 8px;
        }

        .information {
            order: 3;
        }

        .text-blocks-container {
            order: 3;
        }

        .buttons-wrapper {
            order: 3;
        }
    }

    .buttons-right-position-helper {
        position: absolute;
        right: 16px;
        top: 16px;
        z-index: 1;
    }

    .product-image {
        width: 256px;
        height: 154px;

        flex-shrink: 0;

        background-position: center;
        background-size: cover;
        border-radius: 8px;
    }

    .product-info-wrapper {
        width: 100%;
        position: relative;

        display: flex;
        flex-direction: column;

        .title {
            @include H3-Regular;
            color: var(--primary);
            margin-bottom: 8px;
        }

        .description {
            @include H6-Regular;
            color: var(--secondary-dark-1);
            line-height: 18px;

            &.tiny {
                @include H7-Regular;
            }

            &.bold {
                margin-top: 8px;

                @include H6-Medium;
                color: var(--secondary-dark-3);
            }
        }

        .elements-hidden-wrapper {
            width: 100%;
            max-height: 126px;
            flex-shrink: 0;

            display: flex;
            flex-direction: column;

            position: relative;
            overflow: hidden;

            &.remove-max-height {
                max-height: unset;
            }

            &.padding-bottom {
                padding-bottom: 38px;
            }

            .information {
                margin: 8px 0;
                flex-shrink: 0;
            }

            .text-block-bullet-list {
                margin-top: 16px;
                flex-shrink: 0;
            }

            .divider-generic-wrapper {
                width: 100%;

                position: absolute;
                bottom: 0;

                cursor: pointer;
                z-index: 2;
            }

            &.add-after {
                &:after {
                    content: '';
                    width: 100%;
                    height: 76px;

                    position: absolute;
                    bottom: 0;
                    left: 0;

                    background-image: var(--gradient-bottom-fade);
                    z-index: 1;
                }
            }
        }

        .text-blocks-container {
            width: 100%;

            display: flex;
            flex-direction: row;
            gap: 24px;

            .text-blocks-wrapper {
                width: fit-content;
            }
        }

        .buttons-wrapper {
            width: 100%;
            margin-top: 24px;
            display: flex;
            flex-direction: row;
            gap: 24px;

            :deep(.button-raised) {
                min-width: fit-content;
                width: 192px
            }

            :deep(.button-elevated) {
                min-width: fit-content;
                width: 192px
            }
        }

        &.mobile {
            .mobile-information {
                width: 100%;
                margin: 24px 0 8px 0;
            }

            .text-blocks-container {
                flex-direction: column;
            }
        }
    }

    &.mobile {
        &.apply-mobile-header-product-slider {
            flex-direction: column;

            border: none;

            .product-image {
                margin: 0 auto;
            }
        }

        .product-info-wrapper {
            .elements-hidden-wrapper {
                max-height: 72px;

                &.remove-max-height {
                    max-height: unset;
                }

                &.padding-bottom {
                    padding-bottom: 38px;
                }
            }

            .text-blocks-container {
                flex-direction: column;
            }
        }
    }
}
</style>
